import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Send, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    shiftPreference: '',
    message: '',
    privacyConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, shiftPreference: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, privacyConsent: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacyConsent) {
      toast.error('Please accept the privacy policy to continue');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          shift_preference: formData.shiftPreference,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Application submitted confidentially', {
          description: 'Lisa will be in touch with you soon.',
        });
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          shiftPreference: '',
          message: '',
          privacyConsent: false
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit application', {
        description: 'Please try SMS instead: 0429 085683',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="relative py-24 lg:py-32 bg-[#0A0A0C]"
      data-testid="contact-section"
    >
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_top_right,_rgba(224,40,82,0.08)_0%,_transparent_50%)]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left Column - Info */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <span className="font-mono text-xs text-[#E02852] tracking-[0.3em] uppercase block mb-4">
                Get Started
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F7F6F2] tracking-tight mb-6">
                Start Your Journey Here
              </h2>
              <p className="text-lg text-[#A1A1AA] leading-relaxed">
                Whether you have previous experience in the industry or you're curious about opportunities, 
                finding the right place to start can make a huge difference. Complete a confidential 
                application and we'll be in touch about roles that suit you.
              </p>
            </div>

            {/* Contact Card */}
            <div className="card-glass rounded-2xl p-8 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[rgba(224,40,82,0.15)] flex items-center justify-center">
                  <MessageCircle className="text-[#E02852]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#F7F6F2] font-medium text-lg">Prefer to SMS?</h3>
                  <p className="text-[#A1A1AA] text-sm">Contact Lisa directly</p>
                </div>
              </div>

              <a
                href="sms:0429085683?body=Hi%20Lisa,%20I'm%20interested%20in%20confidential%20employment%20opportunities%20in%20Melbourne."
                className="flex items-center justify-center gap-3 w-full btn-primary py-4 rounded-xl text-lg font-medium magnetic-btn"
                data-testid="contact-sms-btn"
              >
                <MessageCircle size={20} />
                <span>SMS Lisa: 0429 085683</span>
              </a>

              <p className="text-center text-xs text-[#71717A]">
                Please SMS only — no direct telephone calls
              </p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Lock, text: '100% Confidential' },
                { icon: CheckCircle, text: 'Quick Response' },
              ].map((badge, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 text-[#A1A1AA]"
                >
                  <badge.icon size={18} className="text-[#D4AF37]" />
                  <span className="text-sm">{badge.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="card-glass rounded-2xl p-8 lg:p-10">
              <div className="flex items-center gap-2 mb-8">
                <Lock size={16} className="text-[#22C55E]" />
                <span className="font-mono text-xs text-[#22C55E] tracking-wider uppercase">
                  Confidential Application
                </span>
              </div>

              {isSubmitted ? (
                <motion.div
                  className="text-center py-12"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="w-16 h-16 rounded-full bg-[rgba(34,197,94,0.15)] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-[#22C55E]" size={32} />
                  </div>
                  <h3 className="font-serif text-2xl text-[#F7F6F2] mb-3">
                    Application Received
                  </h3>
                  <p className="text-[#A1A1AA] mb-6">
                    Thank you for your interest. Lisa will be in touch shortly.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-[#E02852] hover:text-[#F7F6F2] transition-colors text-sm"
                  >
                    Submit another application
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-[#A1A1AA] text-sm">
                      Full Name *
                    </Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="form-input rounded-lg py-3 px-4"
                      placeholder="Your name (or pseudonym)"
                      data-testid="input-fullname"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#A1A1AA] text-sm">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input rounded-lg py-3 px-4"
                        placeholder="0400 000 000"
                        data-testid="input-phone"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#A1A1AA] text-sm">
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input rounded-lg py-3 px-4"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="shiftPreference" className="text-[#A1A1AA] text-sm">
                      Preferred Shift/Role
                    </Label>
                    <Select onValueChange={handleSelectChange} value={formData.shiftPreference}>
                      <SelectTrigger 
                        className="form-input rounded-lg py-3 px-4 h-auto"
                        data-testid="select-shift"
                      >
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181F] border-[rgba(255,255,255,0.1)]">
                        <SelectItem value="daytime">Daytime Shifts</SelectItem>
                        <SelectItem value="evening">Evening Shifts</SelectItem>
                        <SelectItem value="overnight">Overnight Shifts</SelectItem>
                        <SelectItem value="flexible">Flexible / Any</SelectItem>
                        <SelectItem value="casual">Casual / On-Call</SelectItem>
                        <SelectItem value="fulltime">Full-Time</SelectItem>
                        <SelectItem value="reception">Reception / Admin</SelectItem>
                        <SelectItem value="other">Other / Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#A1A1AA] text-sm">
                      Message / Questions
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="form-input rounded-lg py-3 px-4 min-h-[120px] resize-none"
                      placeholder="Tell us about yourself or ask any questions..."
                      data-testid="input-message"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="privacyConsent"
                      checked={formData.privacyConsent}
                      onCheckedChange={handleCheckboxChange}
                      className="mt-1 border-[rgba(255,255,255,0.2)] data-[state=checked]:bg-[#E02852] data-[state=checked]:border-[#E02852]"
                      data-testid="checkbox-privacy"
                    />
                    <Label 
                      htmlFor="privacyConsent" 
                      className="text-sm text-[#A1A1AA] leading-relaxed cursor-pointer"
                    >
                      I understand my information will be handled confidentially and used only 
                      for employment enquiries. *
                    </Label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary py-4 rounded-xl text-base font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>Submit Confidential Application</span>
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-[#71717A]">
                    All applications are reviewed personally by Lisa
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
